import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useImmer } from 'use-immer';

import Grid from '@mui/material/Grid2';
import { 
  NumberFeature,
  ModelBase,
  DisablableOption
} from "@models";
import { 
  Character,
  CharacterFeature,
  BasicCreatureInstance,
  SummonInstance,
  CreatureAbility,
  MinionAbility,
  UpgradableNumberFeature,
  // EncounterLog,
  CreatureSpellAsAbility,
  Spell,
  TransformInstance,
  CharacterAction,
  SummonOption2,
  CharacterAbility,
  CharacterSpell,
  DamageTypeOverrides,
  CreatureGift,
  CharacterCondition,
  RollPlus,
  RolledValue,
  iCreatureSpell,
  ComplexCreatureInstance,
} from "@dndModels";
import { SIZE_MAP, XP_FOR_CR } from "@dndModels/Constants";
import { AbilityScoreType, SpellLevel } from '@dndModels/Types';

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import LoadingBox from "@components/input/LoadingBox";

import CharacterResourceBoxesNew from "@dndComponents/character/misc/CharacterResourceBoxes";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ConditionBar from "@dndComponents/other/ConditionBar";
import CreatureEffect from "@dndComponents/character/spells_and_abilities/effects/CreatureEffect";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import HPInput from "@dndComponents/other/HPInput";
import CreatureActionInput from "@dndComponents/creature/CreatureActionInput";
import ToastBox from '@components/display/ToastBox';
import Roller from '@dndComponents/other/Roller';
import StringBox from '@components/input/strings/StringBox';
import NotesButton from '@components/input/buttons/NotesButton';
import BasicImageButton from '@components/input/buttons/icon_buttons/BasicImageButton';
import FileDropzone from '@components/input/FileDropzone';

interface Props { 
  character?: Character;
  creature_instance: BasicCreatureInstance | SummonInstance | ComplexCreatureInstance;
  trim?: boolean;
  true_id: string;
  view_only?: boolean;
  onChange?: (changed: BasicCreatureInstance | SummonInstance | ComplexCreatureInstance) => void;
  onSummon?: (spell: CharacterAbility | CharacterSpell | Spell | CreatureAbility | MinionAbility, level: SpellLevel, selected_option: SummonOption2) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CreatureInstanceInput({ 
  character,
  creature_instance,
  trim = false,
  true_id,
  view_only = false,
  onChange,
  onSummon
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload_resources, setReloadResources] = useState(false);
  const [selected_spell, setSelectedSpell] = useState<Spell | null>(null);
  const [prev_true_id, setTrueId] = useState(true_id);
  const [ping, setPing] = useState(false);
  const [expanded, setExpanded] = useImmer<{ [section: string]: boolean }>({});
  const mounted = useRef(false);
  const [creature_gifts, setCreatureGifts] = useState<CreatureGift[]>([]);
  const [popoverAbility, setPopoverAbility] = useState("");
  const [popoverTime, setPopoverTime] = useState(-1);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [changing_name, setChangingName] = useState(false);
  const [notes_open, setNotesOpen] = useState(false);
  const [image_open, setImageOpen] = useState(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    setReloading(true);
  }

  const resourcesChanged = () => {
    console.log("resourcesChanged()"); // I think it's not working because it's using a clone of the object somewhere
    // setReloading(true);
    setReloadResources(true);
  }

  const loadCreature = useCallback(async () => {
    if (creature_instance instanceof BasicCreatureInstance) {
      // debugger;
      setLoading(true);
      if (creature_instance.creature) {
        await api.loadObject(creature_instance.creature);
        await creature_instance.connectCreature(creature_instance.creature);
      }
      const res = await char_util.getCreatureGiftsForCreature(creature_instance);
      setCreatureGifts(res);
      setLoading(false);
    } 
  }, [creature_instance, setLoading, setCreatureGifts]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      creature_instance.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      creature_instance.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
      loadCreature();
    }
  }, [fetched, creature_instance, setFetched, loadCreature]);

  useEffect(() => {
    if (reloading) {
      setReloading(false)
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (reload_resources) {
      setReloadResources(false)
    }
  }, [reload_resources, setReloadResources]);

  useEffect(() => {
    if (true_id !== prev_true_id) {
      setTrueId(true_id);
      setReloading(true)
    }
  }, [true_id, prev_true_id, setTrueId, setReloading]);

  const disabled = api.user === null || !api.online || (character !== undefined && !(character.owner_id === api.user._id || (character.campaign && character.campaign.owner_id === api.user._id)));
  
  const ci = creature_instance;

  // console.log(creature);
  const renderExtras = () => {
    return (
      <>
        <ToastBox
          openTime={ popoverTime }
        >
          { renderRoll() }
        </ToastBox>
      </>
    );
  }

  const renderRoll = () => {
    return (
      <Roller 
        name={popoverAbility}
        rolled_values={roll}
        type="Ability Check" 
      />
    );
  }

  const renderHP = () => {
    // debugger;
    const hd_string = ci.hit_dice_string;
    // return (
    //   <p><strong>HP</strong> {creature.hp.max} ({hd_string})</p>
    // );
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={6}>
          <HPInput
            char={ci}
            disabled={disabled}
          />
        </Grid>
        <Grid size={6}></Grid>
        { ci.hit_dice.filter(o => o.size > 0).length > 0 &&
          <Grid size={6} className="centered">({hd_string})</Grid>
        }
      </Grid>
    )
  }

  const renderStat = (stat: AbilityScoreType) => {
    return (
      <tr>
        <th>{stat}</th>
        <td>
          {ci.current_ability_scores.getAbilityScore(stat)}
        </td>
        <td className="clickable" 
          onClick={() => {
            const rolls: RollPlus[] = [];
            const roll_plus = new RollPlus();
            roll_plus.ability_score = stat;
            rolls.push(roll_plus);
            // const { advantage } = creature instanceof CreatureInstance || creature instanceof SummonInstance ? { advantage: 0 } : char_util.getAdvantage(creature, "Attack", stat);
            const advantage = 0;
            setRoll(char_util.rollD20({ char_interface: ci, rolls, type: "Ability Check", advantage }));
            setPopoverAbility(stat);
            setPopoverTime(common_util.get_time_number());
          }}>
          {common_util.addPlusMaybe(ci.current_ability_scores.getModifier(stat))}
        </td>
        <td className="clickable" 
          onClick={() => {
            const rolls: RollPlus[] = [];
            const roll_plus = new RollPlus();
            roll_plus.flat = ci.saving_throw(stat);
            rolls.push(roll_plus);
            // const { advantage } = creature instanceof CreatureInstance || creature instanceof SummonInstance ? { advantage: 0 } : char_util.getAdvantage(creature, "Attack", stat);
            const advantage = 0;
            setRoll(char_util.rollD20({ char_interface: ci, rolls, type: "Saving Throw", advantage }));
            setPopoverAbility(stat + " Saving Throw");
            setPopoverTime(common_util.get_time_number());
          }}>
          {common_util.addPlusMaybe(ci.saving_throw(stat))}
        </td>
      </tr>
    );
  }

  const renderStats = () => {
    return (
      <div className="stats">
        <table className="physical abilities-saves">
          <thead>
            <tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>
          </thead>
          <tbody>
            { renderStat("STR") }
            { renderStat("DEX") }
            { renderStat("CON") }
          </tbody>
        </table>
        <table className="mental abilities-saves">
          <thead>
            <tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>
          </thead>
          <tbody>
            { renderStat("INT") }
            { renderStat("WIS") }
            { renderStat("CHA") }
          </tbody>
        </table>
      </div>
    );
  }

  const renderSkill = ({ skill_id, prof_level, skill_name, modifier }: { skill_id: string, prof_level: number, skill_name: string, modifier: number }) => {
    return (
      <span key={skill_id}>
        { skill_name } {common_util.addPlusMaybe(modifier)}
      </span>
    );
  }

  const renderSkills = () => {
    if (ci.skill_proficiencies2.length > 0) {
      return (
        <p>
          <strong>Skills</strong>&nbsp;
          {ci.skill_proficiencies2.map((sp, key) => {
            return (
              <span key={sp.skill_id} 
                className="clickable" 
                onClick={() => {
                  const rolls: RollPlus[] = [];
                  const roll_plus = new RollPlus();
                  roll_plus.flat = sp.modifier;
                  rolls.push(roll_plus);
                  // const { advantage } = creature instanceof CreatureInstance || creature instanceof SummonInstance ? { advantage: 0 } : char_util.getAdvantage(creature, "Attack", stat);
                  const advantage = 0;
                  setRoll(char_util.rollD20({ char_interface: ci, rolls, type: "Skill Check", advantage }));
                  setPopoverAbility(sp.skill_name);
                  setPopoverTime(common_util.get_time_number());
                }}>
                { key > 0 && ", " }
                { renderSkill(sp) }
              </span>
            );
          })}
        </p>
      );
    }
    return null;
  }

  const renderVulnerabilities = () => {
    if (ci.vulnerabilities_string.length > 0) {
      return (
        <p><strong>Vulnerabilities</strong> {ci.vulnerabilities_string}</p>
      );
    }
    return null;
  }

  const renderResistances = () => {
    if (ci.resistances_string.length > 0) {
      return (
        <p><strong>Resistances</strong> {ci.resistances_string}</p>
      );
    }
    return null;
  }

  const renderImmunities = () => {
    if (ci.immunities_string.length > 0) {
      if (ci.condition_immunities.length > 0) {
        return (
          <p><strong>Immunities</strong> {ci.immunities_string}; {ci.condition_immunities_string}</p>
        );
      } else {
        return (
          <p><strong>Immunities</strong> {ci.immunities_string}</p>
        );
      }
    } else if (ci.condition_immunities.length > 0) {
      return (
        <p><strong>Immunities</strong> {ci.condition_immunities_string}</p>
      );
    }
    return null;
  }

  const renderSenses = () => {
    // debugger;
    const senses_string = ci.senses_string.length > 0 ? ` ${ci.senses_string};` : "";
    return (
      <p><strong>Senses</strong>{senses_string} Passive Perception {ci.passive_perception}</p>
    );
  }

  const renderLanguages = () => {
    return (
      <p><strong>Languages</strong> {ci.languages}</p>
    );
  }

  const renderCreatureInstanceConditions = () => {
    const active_conditions = creature_instance.condition_hash;
    return (
      <ConditionBar
        char_int={character}
        active_conditions={active_conditions}
        starred={creature_instance.favorite_conditions}
        immune={creature_instance.condition_immunities}
        // extra_ids={["Any"]}
        disabled={disabled}
        onChange={(condition_id: string, level: number) => {
          const new_obj = creature_instance;
          if (level === 0) {
            // delete new_obj.condition_hash[condition_id];
            new_obj.conditions = new_obj.conditions.filter(o => o.source_id !== condition_id);
          } else {
            let char_cond = new_obj.conditions.find(o => o.source_id === condition_id);
            if (!char_cond) {
              const cond = data_util.conditions.find(o => o._id === condition_id);
              if (cond) {
                char_cond = new CharacterCondition();
                char_cond.source_id = condition_id;
                // char_cond.copy_source({ char: new_obj, feature_source: cond });
                new_obj.conditions.push(char_cond);
              }
            }
            if (char_cond) {
              char_cond.level = level;
            }
            // new_obj.condition_hash[condition_id] = { level, source_true_id: "" };
          }
          if (onChange) {
            onChange(new_obj);
          }
          setPing(!ping);
        }}
        onStar={(id: string) => {
          const new_obj = creature_instance;
          if (new_obj.favorite_conditions.includes(id)) {
            new_obj.favorite_conditions = new_obj.favorite_conditions.filter(o => o !== id);
          } else {
            new_obj.favorite_conditions.push(id);
          }
          // const log = new EncounterLog();
          // log.field = "favorite_conditions";
          // log.value = new_obj.favorite_conditions;
          if (onChange) {
            onChange(new_obj);
          }
          setPing(!ping);
        }}
        onImmune={(id: string) => {
          const new_obj = creature_instance;
          if (new_obj.condition_immunities.includes(id)) {
            new_obj.condition_immunities = new_obj.condition_immunities.filter(o => o !== id);
          } else {
            new_obj.condition_immunities.push(id);
          }
          // const log = new EncounterLog();
          // log.field = "condition_immunities";
          // log.value = new_obj.condition_immunities;
          if (onChange) {
            onChange(new_obj);
          }
          setPing(!ping);
        }}
      />
    );
  }

  const renderCR = () => {
    if (ci instanceof BasicCreatureInstance) {
      return (
        <p><strong>CR</strong> {ci.challenge_rating} (XP {common_util.commatizeNumber(XP_FOR_CR[ci.challenge_rating])}; PB +{ci.proficiency_modifier})</p>
      );
    }
    return null;
  }

  const renderCF = (cf: CharacterFeature) => {
    // return (
    //   <p key={cf.true_id}><strong><em>{cf.name}.</em></strong> 
    //     <DisplayStringWithReplacements display_string={cf.description} cfs={creature} />
    //   </p>
    // );
    
    // console.log(cf);
    if (cf.floating_feature instanceof CreatureAbility || 
      cf.floating_feature instanceof MinionAbility) {
      const floating_feature = cf.floating_feature;
      const dt_overrides: DamageTypeOverrides[] = [];
      if (floating_feature instanceof CreatureAbility && creature_instance instanceof BasicCreatureInstance) {
        const dt_override_tables = creature_instance.creature_gifts.map(o => o.damage_type_overrides_table);
        dt_overrides.push(...dt_override_tables.flatMap(o => o.entries).filter(o => api.fitAbilityLimit(floating_feature, o.ability_limit)));
      }
      return (
        <Grid container spacing={0} direction="row" key={cf.true_id}>
          <Grid size={12}>
            <CreatureActionInput
              char={character}
              obj={creature_instance}
              char_feature={cf}
              action={floating_feature}
              dt_overrides={dt_overrides}
              disabled={disabled}
              trim={trim}
              // log_it={cf.name.includes("Dreadful")}
              onChange={() => {
                if (onChange) {
                  onChange(creature_instance);
                }
                setPing(!ping);
              }}
              onClick={() => {
                setExpanded(draft => {
                  if (draft[`action_${floating_feature._id}`]) {
                    delete draft[`action_${floating_feature._id}`];
                  } else {
                    draft[`action_${floating_feature._id}`] = true;
                  }
                });
              }}
              onSummon={(selected_option: SummonOption2) => {
                if (onSummon) {
                  if (floating_feature instanceof CreatureSpellAsAbility) {
                    if (floating_feature.spell) {
                      onSummon(floating_feature.spell, floating_feature.spell.level, selected_option);
                    }
                  } else {
                    onSummon(floating_feature, 0, selected_option);
                  }
                }
              }}
            />
          </Grid>
          { expanded[`action_${floating_feature._id}`] &&
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <DisplayStringWithReplacements display_string={cf.description} cfs={ci} />
              </Grid>
            </>
          }
        </Grid>
      )
    }
    return null;
  }

  const renderLegResistances = () => {
    if (ci.legendary_resistances > 0) {
      return (
        <p>
          <CharacterResourceBoxesNew 
            used={creature_instance.used_legendary_resistances}
            total={creature_instance.legendary_resistances}
            true_total={creature_instance.legendary_resistances}
            created={0}
            label={<strong><em>Legendary Resistance ({ci.legendary_resistances}/Day).</em></strong>}
            type={"Legendary Resistances"}
            marginLeft={0}
            show_type={false}
            show_reset
            shared={false}
            disabled={view_only}
            onChange={(used: number) => {
              if (!view_only) {
                const new_obj = creature_instance as BasicCreatureInstance;
                new_obj.used_legendary_resistances = used;
                new_obj.subscription_holder.resourcesChanged();
                if (onChange) {
                  onChange(new_obj);
                }
                setPing(!ping);
              }
            }}
          />
          If the {ci.small_name} fails a saving throw, it can choose to succeed instead.
        </p>
      );
    }
    return null;
  }

  const renderTraits = () => {
    const traits = ci.traits;
    if (traits.length > 0 || ci.legendary_resistances > 0) {
      return (
        <>
          <p className="monster-header">Traits</p>
          { traits.map((cf => renderCF(cf)))}
          { renderLegResistances() }
        </>
      );
    }
  }

  const renderActions = () => {
    // debugger;
    const action_fbs = ci.action_cfs;
    if (action_fbs.length > 0) {
      return (
        <>
          <p className="monster-header">Actions</p>
          { action_fbs.map((cf => renderCF(cf)))}
          { renderSpells() }
        </>
      );
    }
    return null;
  }

  const renderBonusActions = () => {
    const action_fbs = ci.bonus_actions;
    if (action_fbs.length > 0) {
      return (
        <>
          <p className="monster-header">Bonus Actions</p>
          { action_fbs.map((cf => renderCF(cf)))}
        </>
      );
    }
    return null;
  }

  const renderReactions = () => {
    const action_fbs = ci.reactions;
    if (action_fbs.length > 0) {
      return (
        <>
          <p className="monster-header">Reactions</p>
          { action_fbs.map((cf => renderCF(cf)))}
        </>
      );
    }
    return null;
  }

  const renderLegendaryActions = () => {
    const legendary_action_fbs = ci.legendary_actions;
    if (legendary_action_fbs.length > 0) {
      return (
        <>
          <p className="monster-header">
            <CharacterResourceBoxesNew 
              used={creature_instance.used_legendary_actions}
              total={creature_instance.legendary_actions_per_round}
              true_total={creature_instance.legendary_actions_per_round}
              created={0}
              type={"Legendary Actions"}
              show_type={false}
              label={<strong><em>Legendary Actions</em></strong>}
              marginLeft={0}
              shared={false}
              disabled={view_only}
              show_reset
              onChange={(used: number) => {
                if (!view_only) {
                  const new_obj = creature_instance as BasicCreatureInstance;
                  new_obj.used_legendary_actions = used;
                  new_obj.subscription_holder.resourcesChanged();
                  if (onChange) {
                    onChange(new_obj);
                  }
                  setPing(!ping);
                }
              }}
            />
          </p>
          <p>
            The {ci.small_name} can	take {ci.legendary_actions_per_round} legendary actions, 
            choosing from the options below. Only one legendary action option can be used at a time 
            and only at the end of another creature's turn. 
            The {ci.small_name} regains spent legendary actions at the start of its turn.
          </p>
          { legendary_action_fbs.map((cf => renderCF(cf)))}
        </>
      );
    }
    return null;
  }

  const renderLairActions = () => {
    const cfs = ci.lair_actions;
    if (cfs.length > 0) {
      return (
        <>
          <p>
            One Lair Action happens each round on Initiative Count 20 so long as it's in its lair. 
            A different Lair Action should happen each round.
          </p>
          { cfs.map((cf => renderCF(cf)))}
        </>
      );
    }
    return null;
  }

  const renderResources = () => {
    const return_us: any[] = [];
    if (!reload_resources) {
      if (creature_instance.use_resources.length > 0) {
        return_us.push(
          <Grid
            key="resources"
            className='header'
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>Resources</Grid>
        );
      }
      for (const r of creature_instance.use_resources) {
        const resource = data_util.resources.find(o => o._id === r.resource_id);
        if (resource) {
          return_us.push(
            <Grid
              key={r.resource_id}
              size={{
                xs: 12
              }}>
              <CharacterResourceBoxesNew 
                label={resource.use_name}
                // type={resource.use_name}
                show_type={false}
                used={r.used} 
                total={r.total} 
                true_total={r.total} 
                created={0} 
                shared={false}
                onChange={(used: number) => {
                  const r2 = creature_instance.use_resources.find(o => o.resource_id === r.resource_id);
                  if (r2) {
                    r2.used = used;
                    // const log = new EncounterLog();
                    // log.field = `resources`;
                    // log.value = creature_instance.use_resources;
                    if (onChange) {
                      onChange(creature_instance);
                    }
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
          );
        }
      }
    }
    return return_us;
  }

  const getDisplaySpells = (spell_list: iCreatureSpell[]) => {
    const display_spells: any[] = [];
    for (let i = 0; i < spell_list.length; ++i) {
      const c_spell = spell_list[i];
      display_spells.push(
        <span key={c_spell.spell_id}>
          { i > 0 && ", " }
          <DisplayObjects type="spell" ids={[c_spell.spell_id]} 
            onClick={(obj: ModelBase) => {
              const new_spell = obj as Spell;
              if (selected_spell && new_spell._id === selected_spell._id) {
                setSelectedSpell(null);
              } else {
                setSelectedSpell(new_spell);
              }
            }} 
          />
          { c_spell.extra_text ? c_spell.extra_text : (c_spell.cast_level ? ` (Cast at Level ${c_spell.cast_level})` : "") }
        </span>
      );
    }
    return display_spells;
  }

  const renderSpells = () => {
    // debugger;
    const levels = Object.keys(creature_instance.use_spells).map(o => +o).sort((a,b) => { return +a < +b ? -1 : 1; });
    let return_us: any[] = [];
    if (levels.length > 0) {
      return_us.push(
        <Grid key="spells_header" size={12}
          className="padded">
          <b className="clickable" onClick={() => { 
            setExpanded(draft => {
              if (draft.spells) {
                delete draft.spells;
              } else {
                draft.spells = true;
              }
            });
          }}>Spells</b>
        </Grid>
      );
    }
    if (expanded.spells) {
      if (creature_instance.spellcasting_description !== "") {
        return_us.push(
          <Grid key="spellcasting_desc" size={12}
            className="padded">
            <DisplayStringWithReplacements
              display_string={creature_instance.spellcasting_description} cfs={creature_instance}
            />
          </Grid>
        );
      }
      const slot: any[] = [];
      const at_will: any[] = [];
      const x_day: any[] = [];
      for (const level of levels) {
        const spells_of_level = creature_instance.use_spells.filter(o => o.spell_level === level);
        if (level === 0 && spells_of_level.length > 0) {
          at_will.push(
            <Grid key={level} size={12}
              className="padded">
              Cantrips:&nbsp;
              <DisplayObjects type="spell" ids={spells_of_level.map(o => o.spell_id)} 
                onClick={(obj: ModelBase) => {
                  const new_spell = obj as Spell;
                  if (selected_spell && new_spell._id === selected_spell._id) {
                    setSelectedSpell(null);
                  } else {
                    setSelectedSpell(new_spell);
                  }
                }} 
              />
            </Grid>
          );
        } else {
          // debugger;
          let level_name = "";
          switch(level) {
            case 1: level_name = "1st"; break;
            case 2: level_name = "2nd"; break;
            case 3: level_name = "3rd"; break;
            default: level_name = `${level}th`; break;
          }
          const at_will_spells = spells_of_level.filter(o => o.cast_type === "At Will");
          if (at_will_spells.length > 0) {
            const display_spells = getDisplaySpells(at_will_spells);
            at_will.push(
              <Grid key={level} size={12} className="padded">
                At Will:&nbsp;{ display_spells }
              </Grid>
            );
          }
          const slot_spells = spells_of_level.filter(o => o.cast_type === "Slot");
          const slot_count = creature_instance.use_slots2.find(o => o.resource_id === `Slot: ${level}`);
          if (slot_spells.length > 0 && slot_count && slot_count.total > 0) {
            const display_spells = getDisplaySpells(slot_spells);
            slot.push(
              <Grid key={level} size={12} className="padded">
                { level_name } level{ slot_count ? ` (${slot_count.total} Slots)` : "" }:&nbsp;{ display_spells }
              </Grid>
            );
            if (creature_instance instanceof BasicCreatureInstance && !reload_resources) {
              slot.push(
                <Grid key={`${level}_slots`} size={12}
                  className="padded">
                  <CharacterResourceBoxesNew 
                    used={creature_instance.used_slots[level] || 0}
                    total={slot_count.total}
                    true_total={slot_count.total}
                    created={0}
                    show_type={false}
                    shared={false}
                    onChange={(used: number) => {
                      const new_obj = creature_instance as BasicCreatureInstance;
                      new_obj.used_slots[level] = used;
                      // const log = new EncounterLog();
                      // log.field = "used_slots";
                      // log.value = new_obj.used_slots;
                      
                      if (onChange) {
                        onChange(new_obj);
                      }
                      setPing(!ping);
                    }}
                  />
                </Grid>
              );
            }
          }
          const x_day_spells = spells_of_level.filter(o => o.cast_type === "X/Day");
          if (x_day_spells.length > 0) {
            // I should probably change this to happen on copy/connectCreature
            const spellcasting_entry = creature_instance.creature ? creature_instance.creature.spellcasting_table.getEntry(creature_instance.challenge_rating) : undefined;
            if (spellcasting_entry) {
              const spell_ids_by_x: { [x: number]: string[] } = {};
              for (const x_day_spell of x_day_spells) {
                const x = spellcasting_entry.spells.filter(o => o.spell_id === x_day_spell.spell_id)[0].x;
                if (x) {
                  if (!spell_ids_by_x[x]) {
                    spell_ids_by_x[x] = [];
                  }
                  spell_ids_by_x[x].push(x_day_spell.spell_id);
                }
              }
              if (!reload_resources) {
                for (const ex of Object.keys(spell_ids_by_x).map(o => +o)) {
                  const spell_ids = spell_ids_by_x[ex];
                  const display_spells = getDisplaySpells(x_day_spells.filter(o => o.x === ex));
                  x_day.push(
                    <Grid key={`${level}_${ex}`} size={12} className="padded">
                      { level_name } level ({ ex }/Day{ spell_ids.length > 1 ? " each" : "" }):&nbsp;{ display_spells }
                    </Grid>
                  );
                }
              }
            }
          }
        }
      }
      return_us = [...return_us,...at_will,...x_day,...slot];
      
      if (selected_spell) {
        const c_spell = creature_instance.use_spells.find(o => o.spell_id === selected_spell._id);
        return_us.push(
          <Grid
            key="selected_spell"
            container
            spacing={0}
            direction="row"
            className="padded"
            size={12}>
            <Grid size={12} style={{ fontWeight: "bold" }}>
              { selected_spell.use_name }
            </Grid>
            <Grid size={12}>
              <DisplayStringWithReplacements
                display_string={selected_spell.description} 
                cfs={creature_instance}
              />
            </Grid>
            { creature_instance instanceof BasicCreatureInstance && selected_spell.effects.map((effect, key) => {
              return (
                <Grid key={key} size={12}>
                  <CreatureEffect 
                    creature_instance={creature_instance} 
                    effect={effect} 
                    spell={selected_spell}
                    c_spell={c_spell}
                    processing={false} 
                    disabled={disabled} 
                    shared={false} 
                    level={selected_spell.level} 
                    wide={false} 
                    casting_time={''} 
                    // log_it
                    onChange={(change_types: string[]) => {
                      throw new Error('Function not implemented.');
                    }} 
                    onCostChange={(costs: { [resource: string]: number; }) => {
                      throw new Error('Function not implemented.');
                    }} 
                    onTransform={() => {
                      throw new Error('Function not implemented.');
                    }} 
                    onSummon={(selected_option: SummonOption2) => {
                      if (onSummon) {
                        onSummon(selected_spell, selected_spell.level, selected_option);
                      }
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        );
      }
    }
    return return_us;
  }

  const onDrop = (acceptedFiles: File[]) => {
    console.log(acceptedFiles);
    if (acceptedFiles.length > 0) {
      const image = acceptedFiles[0];
      const data = new FormData();
      data.append("file", image);
      data.append("upload_preset", "o3zenvnq");
      data.append("cloud_name", "dojs5friw");
      fetch("https://api.cloudinary.com/v1_1/dojs5friw/image/upload", {
        method: "post",
        body: data
      })
        .then(resp => resp.json())
        .then(data => {
          const obj = creature_instance;
          // if (image_type === "Character") {
          //   obj.image_url = data.url;
          // } else {
          //   obj.bg_image_url = data.url;
          // }
          obj.image_url = data.url;
          if (character) {
            character.changed();
          } else if (onChange) {
            onChange(obj);
          }
        })
        .catch(err => console.log(err));
    }
  }
  const renderImageControl = () => {
    return (
      <FileDropzone 
        label={`Drag 'n' drop new Image here` }
        accept={{'image/jpeg': [], 'image/png': []}} 
        onDrop={onDrop} 
      />
    );
  } 

  console.log(ci);
  if (reloading || !fetched || loading) {
    return (<LoadingBox />);
  } else {
    const ci_image_style: React.CSSProperties = { };
    if (ci.image_url !== "" && !notes_open && !image_open)  { 
      ci_image_style.backgroundImage = `url(${ci.image_url})`;
      ci_image_style.backgroundSize = "contain";
    }
    return (
      <div className="stat-block">
        { changing_name ? 
          <StringBox
            name="Name"
            value={ci.name}
            onBlur={(changed: string) => {
              const new_obj = creature_instance;
              new_obj.name = changed;
              if (onChange) {
                onChange(new_obj);
              }
              setChangingName(false);
            }}
          />
        : 
          <h4 className="compendium-hr clickable" onClick={() => {
            setChangingName(true);
          }}>{ci.name}</h4>
        }
        <Grid container spacing={0} direction="row">
          <Grid size={6}>
            <p>
              { ci.use_size === undefined ? "Undefined" : SIZE_MAP[ci.use_size] }&nbsp;
              {ci.creature_type_string}, {ci.alignment}&nbsp;
            </p>
            { !notes_open && !image_open && 
              <>
                <p><strong>AC</strong> {ci.use_ac}{ci.creature && ci.creature.ac_flavor.length > 0 ? ` (${ci.creature.ac_flavor})` : ""}</p>
                { renderHP() }
                <p><strong>Speed</strong> {ci.speed_string}</p>
              </>
            }
          </Grid>
          <Grid size={6} style={ci_image_style}>
            <span style={{ float: "right" }}>
              <NotesButton
                onClick={() => { setNotesOpen(!notes_open); setImageOpen(false); }}
              />
              <BasicImageButton
                onClick={() => { setImageOpen(!image_open); setNotesOpen(false); }}
              />
            </span>
          </Grid>
        </Grid>
        { notes_open ?
          <>
            <StringBox
              name="Notes"
              value={ci.notes}
              disabled={disabled}
              multiline
              onBlur={(changed: string) => {
                const new_obj = creature_instance;
                new_obj.notes = changed;
                if (onChange) {
                  onChange(new_obj);
                }
              }}
            />
          </>
        : image_open ? renderImageControl()
        :
          <>
            { renderStats() }
            { renderSkills() }
            { renderVulnerabilities() }
            { renderResistances() }
            { renderImmunities() }
            { renderSenses() }
            { renderLanguages() }
            { renderCreatureInstanceConditions() }
            { renderCR() }
            { renderResources() }
            { renderTraits() }
            { renderActions() }
            { renderBonusActions() }
            { renderReactions() }
            { renderLegendaryActions() }
            { renderLairActions() }
          </>
        }
        { renderExtras() }
      </div>
    );
  }
}
